// TODO: move all offer-related functions to offers.ts
// https://jira.zalando.net/browse/COLLAB-15413

import { isNil } from 'lodash';

import { canCampaignBeChanged } from './campaign';
import { __t, applyTranslations } from './language';
import { addThousandsSeparator } from './helper/common';
import { checkOfferType, isOffer } from 'utils/offers/offers';
import { hasAnyReleasedPayout } from 'utils/payouts';

import type { Campaign, Payment, StatusLabelType, Subcampaign, SubcampaignOffer, SubcampaignOfferForm, SubcampaignState } from 'types';

applyTranslations(require('./i18n/subcampaign.i18n.json'));

export const SUBCAMPAIGN_STATE_TO_LABEL_TYPE: { [x: string]: StatusLabelType } = {
  pending: 'warning',
  accepted: 'success',
  draft: 'draft',
  negotiations: 'warning',
  revoked: 'neutral',
  inactive: 'neutral'
};

export const SUBCAMPAIGN_OFFER_SORT_VALUES = ['start_date_asc', 'end_date_asc'];

export const SUBCAMPAIGN_STATE_ALL = 'all';
export const SUBCAMPAIGN_STATE_DRAFT = 'draft';
export const SUBCAMPAIGN_STATE_ACCEPTED = 'accepted';
export const SUBCAMPAIGN_STATE_PENDING = 'pending';
export const SUBCAMPAIGN_STATE_REVOKED = 'revoked';
export const SUBCAMPAIGN_STATE_DECLINED = 'declined';

export const SUBCAMPAIGN_REVOKED_COMMENT_CAMPAIGN_END = 'collabary.system.campaign-ended';

export const SUBCAMPAIGN_STATES = [
  SUBCAMPAIGN_STATE_ALL,
  SUBCAMPAIGN_STATE_DRAFT,
  SUBCAMPAIGN_STATE_ACCEPTED,
  SUBCAMPAIGN_STATE_REVOKED,
  SUBCAMPAIGN_STATE_PENDING
];

export const TASK_TAGS_COLORS = {
  pink: '#e2629c',
  lavender: '#be8fd7',
  sky: '#026489',
  pool: '#4dc2ee',
  grass: '#aecc67',
  gold: '#f4c63b'
};
export const TASK_TAGS_COLORS_REVERSE = Object.keys(TASK_TAGS_COLORS)
  .reduce((prevValue, color) => ({ ...prevValue, [TASK_TAGS_COLORS[color]]: color }), {});

export const SUBCAMPAIGN_REVOKED_BY_SYSTEM = 'system';
export const SUBCAMPAIGN_REVOKED_BY_INFLUENCER = 'influencer';
export const SUBCAMPAIGN_REVOKED_BY_BRAND = 'brand';

export const SUBCAMPAIGN_REVOKED_BY = [
  SUBCAMPAIGN_REVOKED_BY_BRAND,
  SUBCAMPAIGN_REVOKED_BY_INFLUENCER,
  SUBCAMPAIGN_REVOKED_BY_SYSTEM
];

export const SUBCAMPAIGN_OFFER_TYPE_OFFER = 'offer';
export const SUBCAMPAIGN_OFFER_TYPE_OTHER = 'other';
export const SUBCAMPAIGN_OFFER_TYPE_QUOTE = 'quote';

export const NUM_DAYS_DEFAULT_POSTPONE = 5;

function checkRevokedComment(subcampaign?: Subcampaign | null, comment?) {
  return subcampaign && subcampaign.revokedComment === comment || false;
}

interface WithState {
  state: SubcampaignState
}

function checkState<T extends WithState>(subcampaign: T | undefined | null, state: string): boolean {
  return subcampaign && subcampaign.state === state || false;
}

export function isPending(subcampaign?: Subcampaign | null): boolean {
  return checkState(subcampaign, SUBCAMPAIGN_STATE_PENDING);
}

export function isAccepted<T extends WithState>(subcampaign?: T | null): boolean {
  return checkState(subcampaign, SUBCAMPAIGN_STATE_ACCEPTED);
}

export function isDraft(subcampaign?: Subcampaign | null): boolean {
  return checkState(subcampaign, SUBCAMPAIGN_STATE_DRAFT);
}

export function isRevoked(subcampaign?: Subcampaign | null): boolean {
  return checkState(subcampaign, SUBCAMPAIGN_STATE_REVOKED);
}

function checkRevokedState(subcampaign: Subcampaign | undefined | null, state: string): boolean {
  return subcampaign && subcampaign.revokedBy === state || false;
}

export function isDeclined(subcampaign?: Subcampaign | null): boolean {
  return isRevoked(subcampaign) && checkRevokedState(subcampaign, SUBCAMPAIGN_REVOKED_BY_INFLUENCER);
}

export function isCanceled(subcampaign?: Subcampaign | null): boolean {
  return isRevoked(subcampaign) && checkRevokedState(subcampaign, SUBCAMPAIGN_REVOKED_BY_BRAND);
}

export function isExpired(subcampaign?: Subcampaign | null): boolean {
  return isRevoked(subcampaign) && checkRevokedState(subcampaign, SUBCAMPAIGN_REVOKED_BY_SYSTEM);
}

export function isExpiredAsCampaignEnded(subcampaign?: Subcampaign | null): boolean {
  return isExpired(subcampaign) && checkRevokedComment(subcampaign, SUBCAMPAIGN_REVOKED_COMMENT_CAMPAIGN_END);
}

export function isOther(subcampaign?: Subcampaign | null): boolean {
  return checkOfferType(subcampaign, SUBCAMPAIGN_OFFER_TYPE_OTHER);
}

export function isQuote(subcampaign?: Subcampaign | null): boolean {
  return checkOfferType(subcampaign, SUBCAMPAIGN_OFFER_TYPE_QUOTE);
}

export function isQuoteProvided(subcampaign?: Subcampaign | null): boolean {
  return checkOfferType(subcampaign, SUBCAMPAIGN_OFFER_TYPE_QUOTE) && !!subcampaign?.offer.quote;
}

export function isAnyOfferMade(subcampaign?: Subcampaign | null): boolean {
  return isOffer(subcampaign) || isOther(subcampaign);
}

export function hasSubcampaignValidAllocatedBudget(subcampaign?: Subcampaign | null) {
  if (!subcampaign || !subcampaign.id) {
    return false;
  }
  // return subcampaign.allocatedInstagramBudget + subcampaign.allocatedYoutubeBudget === subcampaign.offer.budget;
  return false;
}

export function getSubcampaignsForBudgetAllocation(subcampaigns: Array<Subcampaign>): Array<Subcampaign> {
  return subcampaigns
    .filter(s => isAccepted(s) && s.offer.budget && s.offer.budget > 0)
    .sort((s1, s2): number => {
      const s1ValidScore = hasSubcampaignValidAllocatedBudget(s1) ? 1 : 0;
      const s2ValidScore = hasSubcampaignValidAllocatedBudget(s2) ? 1 : 0;

      return s1ValidScore - s2ValidScore; // invalid first
    });
}

export function isNegotiationsReopened(subcampaign?: Subcampaign | null): boolean {
  return subcampaign &&
    !isRevoked(subcampaign) &&
    subcampaign.offer &&
    subcampaign.updatedOffer &&
    subcampaign.offer.state === SUBCAMPAIGN_STATE_ACCEPTED &&
    subcampaign.updatedOffer.state === SUBCAMPAIGN_STATE_PENDING || false;
}

export function shouldAskForShippingAddress(subcampaign?: Subcampaign | null): boolean {
  if (!subcampaign || !subcampaign.offer) {
    return false;
  }

  return !isNegotiationsReopened(subcampaign) && subcampaign.offer.includesShipping
    || (subcampaign.updatedOffer && !subcampaign.offer.includesShipping && subcampaign.updatedOffer.includesShipping)
    || false;
}

export function isInfluencerActionRequired(subcampaign: Subcampaign, campaign: Campaign) {
  return canCampaignBeChanged(campaign) && (isNegotiationsReopened(subcampaign) || isPending(subcampaign)) && !isQuoteProvided(subcampaign);
}

export function getSubcampaignDisplayBudget(subcampaign: Subcampaign, prefix: string = ''): string {
  if (isQuote(subcampaign)) {
    return __t('subcampaign.quote_requested');
  }

  if (isOffer(subcampaign)) {
    return `${prefix}${addThousandsSeparator(subcampaign.offer.budget || 0)}€`;
  }

  return __t('subcampaign.other_arrangement');
}

export function getSubcampaignOfferDisplayBudget(
  offer: SubcampaignOffer | undefined | null | SubcampaignOfferForm,
  prefix: string = ''
): string {
  if (!offer) {
    return '';
  }

  if (offer.type === SUBCAMPAIGN_OFFER_TYPE_QUOTE) {
    return __t('subcampaign.quote_requested');
  }

  if (offer.type === SUBCAMPAIGN_OFFER_TYPE_OFFER) {
    return `${prefix}${addThousandsSeparator(offer.budget || 0)}€`;
  }

  return __t('subcampaign.other_arrangement');
}

export function getAgreedSubcampaigns(subcampaigns: Array<Subcampaign>): Array<Subcampaign> {
  return subcampaigns.filter(isAccepted);
}

export function isOfferDraftAndBlank(offer: SubcampaignOffer | SubcampaignOfferForm): boolean {
  return offer.state === SUBCAMPAIGN_STATE_DRAFT &&
    isNil(offer.budget) &&
    isNil(offer.legalRequirements) &&
    isNil(offer.perks) &&
    isNil(offer.personalMessage) &&
    isNil(offer.type);
}

export function canCancelSubcampaign(subcampaign: Subcampaign): boolean {
  return (!isRevoked(subcampaign) && (isPending(subcampaign) || isAccepted(subcampaign)))
    && !hasAnyReleasedPayout(subcampaign.payouts);
}

export function hasPayments(subcampaignPayments: Payment, variablePaymentsAmount: number) {
  return (subcampaignPayments && subcampaignPayments.agreedBudget > 0) || variablePaymentsAmount > 0;
}

export const isOfferSavedAsDraft = (offer: SubcampaignOfferForm): boolean => offer.state === 'draft' && offer.isDefault === false;

export const getEmptyOffer = (offer: SubcampaignOffer): SubcampaignOffer => ({
  ...offer,

  ...({
    type: 'offer',
    budget: 0,
    perks: '',
    legalRequirements: '',
    personalMessage: '',
    state: 'draft',
    includesShipping: false,
    additionalRequirements: '',
    legallyBinding: false,
    quote: null
  }),

  id: offer.id
});
